import * as XLSX from 'xlsx-js-style';
import {strFromU8, strToU8, unzipSync, zipSync} from 'fflate';
import {map, Observable} from 'rxjs';
import {datedExcelFileName, getWorkbook} from '../../../../common/upload/excel.utils';
import {AppCommonUtils} from '../../../../common/app-common-utils';
import {downloadAny} from '../../../../common/download.utils';
import {Entity} from '../../../../handlers/entity';
import {
    calculatedResidualSourceMeterNumber,
    hasActiveSourceBpm,
    isCalculatedResidual
} from '../../meters/submeter-role.utils';
import {MeterMetadataField, RefdataUtils} from '../../refdata-utils';

const READ_ONLY_COLUMN_COUNT = 6;
const READ_ONLY_TRAILING_COLUMNS = new Set([15, 16, 17]);
const METADATA_COLUMNS: Record<MeterMetadataField, number> = {tenant: 7, floor: 8, tower: 9, wing: 10};
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

type SubmeterMetadataLanguage = 'en' | 'nl';

interface SubmeterMetadataWorkbookLabels {
    sheetName: string;
    filePrefix: string;
    headers: readonly string[];
}

export const SUBMETER_METADATA_WORKBOOK_LABELS: Record<SubmeterMetadataLanguage, SubmeterMetadataWorkbookLabels> = {
    en: {
        sheetName: 'Intermediate meters',
        filePrefix: 'intermediate-meters',
        headers: ['Location ID', 'Location', 'Building code', 'Connection EAN', 'Flowmaps meter ID',
            'Source meter number', 'Meter name', 'Tenant', 'Floor', 'Tower', 'Wing', 'Area', 'Visible',
            'Gross production meter (BPM)', 'Charge point', 'Data source', 'Status', 'Reason']
    },
    nl: {
        sheetName: 'Tussenmeters',
        filePrefix: 'tussenmeters',
        headers: ['Locatie-ID', 'Locatie', 'Gebouwcode', 'EAN aansluiting', 'Flowmaps meter-ID',
            'Bronmeternummer', 'Meternaam', 'Huurder', 'Etage', 'Toren', 'Vleugel', 'Oppervlakte', 'Zichtbaar',
            'Brutoproductiemeter (BPM)', 'Laadpaal', 'Databron', 'Status', 'Reden']
    }
};

export const SUBMETER_METADATA_HEADERS = SUBMETER_METADATA_WORKBOOK_LABELS.en.headers;

export interface SubmeterMetadataRow {
    locationId: string;
    location: string;
    buildingCode: string;
    connectionEan: string;
    meterId: string;
    sourceMeterNumber: string;
    label: string;
    tenant: string;
    floor: string;
    tower: string;
    wing: string;
    area: number | null;
    visible: boolean;
    grossProduction: boolean;
    chargePoint: boolean;
    dataSource: string;
    status: string;
    reason: string;
}

export interface SubmeterMetadataUpdate {
    entity: Entity;
    row: SubmeterMetadataRow;
}

export interface SubmeterMetadataValidation {
    updates: SubmeterMetadataUpdate[];
    errors: string[];
}

export function sourceMeterNumber(entity: Entity): string {
    if (isCalculatedResidual(entity.meter)) {
        return calculatedResidualSourceMeterNumber(entity.connection?.connectionId);
    }
    return (entity.meter.info.meterNumbers || []).filter(Boolean).join(', ')
        || entity.meter.info.accessPointId || '';
}

export function rowForIntermediateMeter(entity: Entity): SubmeterMetadataRow {
    const info = entity.location.info;
    const details = entity.meter.details;
    const visible = (details as any)?.visible !== false;
    const grossProduction = entity.meter.info.type === 'GROSS_PRODUCTION' || details?.type === 'GROSS_PRODUCTION';
    const overlapsSourceBpm = grossProduction
        && entity.meter.info.type !== 'GROSS_PRODUCTION'
        && hasActiveSourceBpm(entity.location, entity.meter.meterId);
    return {
        locationId: entity.location.locationId,
        location: info.name || formatAddress(info.address),
        buildingCode: info.buildingCode || '',
        connectionEan: entity.connection.info.code || '',
        meterId: entity.meter.meterId,
        sourceMeterNumber: sourceMeterNumber(entity),
        label: details?.label || entity.meter.info.label || '',
        tenant: RefdataUtils.getMeterMetadataValue(entity.meter, 'tenant'),
        floor: RefdataUtils.getMeterMetadataValue(entity.meter, 'floor'),
        tower: RefdataUtils.getMeterMetadataValue(entity.meter, 'tower'),
        wing: RefdataUtils.getMeterMetadataValue(entity.meter, 'wing'),
        area: details?.area == null ? null : Number(details.area),
        visible,
        grossProduction,
        chargePoint: (details as any)?.chargePoint === true,
        dataSource: entity.meter.info.surveyor?.name || entity.connection.info.surveyor?.name || '',
        status: visible ? (overlapsSourceBpm ? 'Added to existing BPM' : 'Active') : 'Hidden',
        reason: visible
            ? (overlapsSourceBpm ? 'Added to the active BPM source' : '')
            : (overlapsSourceBpm ? 'Hidden because an active BPM source is already linked' : 'Hidden by user')
    };
}

export function downloadIntermediateMeterMetadata(context: Entity, meters: Entity[]) {
    const language = preferredWorkbookLanguage();
    const labels = SUBMETER_METADATA_WORKBOOK_LABELS[language];
    const workbook = createIntermediateMeterMetadataWorkbook(meters, language);
    const buildingCode = safeFilePart(context.location.info.buildingCode || context.location.info.name || 'location');
    const connectionCode = safeFilePart(context.connection?.info?.code || 'electricity');
    const file = createProtectedIntermediateMeterMetadataFile(workbook);
    downloadAny(new Blob([file], {type: XLSX_MIME_TYPE}),
        datedExcelFileName(`${labels.filePrefix}-${buildingCode}-${connectionCode}`), XLSX_MIME_TYPE);
}

export function createIntermediateMeterMetadataWorkbook(meters: Entity[], language: string = preferredWorkbookLanguage()): XLSX.WorkBook {
    const labels = workbookLabels(language);
    const rows = meters.map(rowForIntermediateMeter);
    const data = [Array.from(labels.headers), ...rows.map(row => rowToArray(row, language))];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const sourceMetadataCells = sourceMetadataReadOnlyCells(meters);
    worksheet['!autofilter'] = {ref: `A1:R${Math.max(2, data.length)}`};
    worksheet['!cols'] = [
        {wch: 38}, {wch: 34}, {wch: 18}, {wch: 24}, {wch: 30}, {wch: 24},
        {wch: 44}, {wch: 24}, {wch: 14}, {wch: 14}, {wch: 14}, {wch: 16}, {wch: 12}, {wch: 28},
        {wch: 16}, {wch: 24}, {wch: 24}, {wch: 52}
    ];
    (worksheet as any)['!readOnlyCells'] = sourceMetadataCells;
    styleMetadataSheet(worksheet, data.length, sourceMetadataCells);
    (worksheet as any)['!protect'] = {password: 'flowmaps'};
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, labels.sheetName);
    return workbook;
}

export function createProtectedIntermediateMeterMetadataFile(workbook: XLSX.WorkBook): Uint8Array {
    const readOnlyCells = readOnlyCellsForWorkbook(workbook);
    const xlsx = XLSX.write(workbook, {type: 'array', bookType: 'xlsx', compression: true, cellStyles: true});
    const archive = unzipSync(new Uint8Array(xlsx));
    const worksheetPath = 'xl/worksheets/sheet1.xml';
    const stylesPath = 'xl/styles.xml';
    const worksheetXml = strFromU8(archive[worksheetPath]);
    const editableStyleIndexes = editableCellStyleIndexes(worksheetXml, readOnlyCells);
    archive[stylesPath] = strToU8(unlockCellStyles(strFromU8(archive[stylesPath]), editableStyleIndexes));
    return zipSync(archive, {level: 6});
}

export function readIntermediateMeterMetadata(file: File): Observable<SubmeterMetadataRow[]> {
    return getWorkbook(file).pipe(map(readIntermediateMeterMetadataWorkbook));
}

export function readIntermediateMeterMetadataWorkbook(workbook: XLSX.WorkBook): SubmeterMetadataRow[] {
    for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json<any[]>(sheet, {header: 1, defval: '', raw: true});
        const headers = data[0] || [];
        if (!hasSupportedHeaders(headers)) continue;
        return data.slice(1)
            .filter(row => row.some(value => normalise(value) !== ''))
            .map(row => arrayToRow(row, headers));
    }
    throw new Error(preferredWorkbookLanguage() === 'nl'
        ? 'Het tussenmeterbestand heeft geen geldige Nederlandse of Engelse indeling. Download een nieuw bestand bij deze elektriciteitsaansluiting.'
        : 'The intermediate meter file has no valid Dutch or English layout. Download a fresh file from this electricity connection.');
}

export function validateIntermediateMeterMetadata(rows: SubmeterMetadataRow[], meters: Entity[]): SubmeterMetadataValidation {
    const errors: string[] = [];
    const updates: SubmeterMetadataUpdate[] = [];
    const byId = new Map(meters.map(entity => [entity.meter.meterId, entity]));
    const seen = new Set<string>();

    rows.forEach((row, index) => {
        const excelRow = index + 2;
        if (!row.meterId) {
            errors.push(`Row ${excelRow}: Flowmaps meter ID is required.`);
            return;
        }
        if (seen.has(row.meterId)) {
            errors.push(`Row ${excelRow}: Flowmaps meter ID ${row.meterId} occurs more than once.`);
            return;
        }
        seen.add(row.meterId);
        const entity = byId.get(row.meterId);
        if (!entity) {
            errors.push(`Row ${excelRow}: meter ${row.meterId} is not linked to this electricity connection.`);
            return;
        }
        if (row.grossProduction && row.chargePoint) {
            errors.push(`Row ${excelRow}: a meter cannot be both a gross production meter and a charge point.`);
        }
        if (isCalculatedResidual(entity.meter) && (row.grossProduction || row.chargePoint)) {
            errors.push(`Row ${excelRow}: the calculated remainder cannot be classified as gross production or charge point.`);
        }
        if (row.area !== null && (!Number.isFinite(row.area) || row.area <= 0)) {
            errors.push(`Row ${excelRow}: area must be a positive number or left empty.`);
        }
        const expected = rowForIntermediateMeter(entity);
        const immutableChecks: Array<[keyof SubmeterMetadataRow, string]> = [
            ['locationId', 'Location ID'],
            ['location', 'Location'],
            ['buildingCode', 'Building code'],
            ['connectionEan', 'Connection EAN'],
            ['meterId', 'Flowmaps meter ID'],
            ['sourceMeterNumber', 'Source meter number']
        ];
        immutableChecks.forEach(([key, label]) => {
            if (normalise(row[key]) !== normalise(expected[key])) {
                errors.push(`Row ${excelRow}: ${label} is read-only and no longer matches Flowmaps.`);
            }
        });
        updates.push({entity, row});
    });

    meters.filter(entity => !seen.has(entity.meter.meterId)).forEach(entity => {
        errors.push(`Meter ${entity.meter.meterId} is missing. Download a fresh file from this electricity connection.`);
    });
    return {updates: errors.length ? [] : updates, errors};
}

function styleMetadataSheet(sheet: XLSX.WorkSheet, rowCount: number, readOnlyCells: Set<string>) {
    for (let row = 0; row < rowCount; row++) {
        for (let col = 0; col < SUBMETER_METADATA_HEADERS.length; col++) {
            const address = XLSX.utils.encode_cell({r: row, c: col});
            const cell = sheet[address] || (sheet[address] = {t: 's', v: ''});
            const readOnly = row === 0 || isReadOnlyColumn(col) || readOnlyCells.has(address);
            cell.s = {
                ...(cell.s || {}),
                fill: {patternType: 'solid', fgColor: {rgb: row === 0 ? 'D9EAF7' : readOnly ? 'EDEDED' : 'FFFFFF'}},
                font: {bold: row === 0, color: {rgb: '000000'}},
                alignment: {vertical: 'center'},
                protection: {locked: readOnly},
                border: {
                    top: {style: 'thin', color: {rgb: 'B7B7B7'}},
                    bottom: {style: 'thin', color: {rgb: 'B7B7B7'}},
                    left: {style: 'thin', color: {rgb: 'B7B7B7'}},
                    right: {style: 'thin', color: {rgb: 'B7B7B7'}}
                }
            };
            if (row > 0 && col === 11) cell.z = '#,##0';
        }
    }
}

function editableCellStyleIndexes(worksheetXml: string, readOnlyCells: Set<string>): Set<number> {
    const result = new Set<number>();
    const worksheet = new DOMParser().parseFromString(worksheetXml, 'application/xml');
    Array.from(worksheet.getElementsByTagName('c')).forEach(cell => {
        const reference = /^([A-Z]+)(\d+)$/.exec(cell.getAttribute('r') || '');
        const style = cell.getAttribute('s');
        if (!reference || style === null) return;
        const cellAddress = cell.getAttribute('r') || '';
        const column = XLSX.utils.decode_col(reference[1]);
        if (!readOnlyCells.has(cellAddress) && !isReadOnlyColumn(column) && column < SUBMETER_METADATA_HEADERS.length
            && Number(reference[2]) > 1) {
            result.add(Number(style));
        }
    });
    return result;
}

function unlockCellStyles(stylesXml: string, editableStyleIndexes: Set<number>): string {
    if (!editableStyleIndexes.size) return stylesXml;
    const styles = new DOMParser().parseFromString(stylesXml, 'application/xml');
    const cellXfs = styles.getElementsByTagName('cellXfs')[0];
    Array.from(cellXfs.children).forEach((xf, styleIndex) => {
        if (!editableStyleIndexes.has(styleIndex)) return;
        xf.setAttribute('applyProtection', '1');
        const protection = styles.createElementNS(cellXfs.namespaceURI, 'protection');
        protection.setAttribute('locked', '0');
        xf.appendChild(protection);
    });
    return new XMLSerializer().serializeToString(styles);
}

function sourceMetadataReadOnlyCells(meters: Entity[]): Set<string> {
    const result = new Set<string>();
    meters.forEach((entity, index) => {
        (Object.keys(METADATA_COLUMNS) as MeterMetadataField[])
            .filter(field => RefdataUtils.hasSourceMeterMetadataValue(entity.meter, field))
            .map(field => XLSX.utils.encode_cell({r: index + 1, c: METADATA_COLUMNS[field]}))
            .forEach(cell => result.add(cell));
    });
    return result;
}

function readOnlyCellsForWorkbook(workbook: XLSX.WorkBook): Set<string> {
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    return firstSheet?.['!readOnlyCells'] || new Set<string>();
}

function rowToArray(row: SubmeterMetadataRow, language: string): any[] {
    return [row.locationId, row.location, row.buildingCode, row.connectionEan, row.meterId,
        row.sourceMeterNumber, row.label, row.tenant, row.floor, row.tower, row.wing,
        row.area as any, booleanCell(row.visible, language), booleanCell(row.grossProduction, language),
        booleanCell(row.chargePoint, language), row.dataSource,
        statusCell(row.status, language), reasonCell(row.reason, language)];
}

function arrayToRow(row: any[], headers: any[]): SubmeterMetadataRow {
    const value = (...names: string[]) => {
        const index = headers.findIndex(header => names.includes(normalise(header).toLocaleLowerCase()));
        return index < 0 ? '' : row[index];
    };
    return {
        locationId: normalise(value('location id', 'locatie-id')),
        location: normalise(value('location', 'locatie')),
        buildingCode: normalise(value('building code', 'gebouwcode')),
        connectionEan: normalise(value('connection ean', 'ean aansluiting')),
        meterId: normalise(value('flowmaps meter id', 'flowmaps meter-id')),
        sourceMeterNumber: normalise(value('source meter number', 'bronmeternummer')),
        label: normalise(value('meter name', 'meternaam')),
        tenant: normalise(value('tenant', 'huurder')),
        floor: normalise(value('floor', 'etage')),
        tower: normalise(value('tower', 'toren')),
        wing: normalise(value('wing', 'vleugel')),
        area: parseArea(value('area', 'oppervlakte')),
        visible: parseBoolean(value('visible', 'zichtbaar'), true),
        grossProduction: parseBoolean(value('gross production meter (bpm)', 'brutoproductiemeter (bpm)'), false),
        chargePoint: parseBoolean(value('charge point', 'laadpaal'), false),
        dataSource: normalise(value('data source', 'databron')),
        status: parseStatus(value('status')),
        reason: parseReason(value('reason', 'reden'))
    };
}

function parseArea(value: any): number | null {
    if (normalise(value) === '') return null;
    return typeof value === 'number' ? value : Number(normalise(value).replace(',', '.'));
}

function booleanCell(value: boolean, language: string): string {
    return language?.toLowerCase().split('-')[0] === 'nl' ? (value ? 'Ja' : 'Nee') : (value ? 'Yes' : 'No');
}

function statusCell(value: string, language: string): string {
    if (language?.toLowerCase().split('-')[0] !== 'nl') return value;
    return value === 'Hidden' ? 'Verborgen' : value === 'Added to existing BPM' ? 'Opgeteld bij bestaande BPM' : 'Actief';
}

function reasonCell(value: string, language: string): string {
    if (language?.toLowerCase().split('-')[0] !== 'nl') return value;
    if (value === 'Added to the active BPM source') return 'Opgeteld bij de actieve BPM-bron';
    if (value === 'Hidden because an active BPM source is already linked') {
        return 'Verborgen omdat al een actieve BPM-bron is gekoppeld';
    }
    return value === 'Hidden by user' ? 'Verborgen door gebruiker' : value;
}

function parseStatus(value: any): string {
    const status = normalise(value);
    if (status === 'Verborgen') return 'Hidden';
    if (status === 'Opgeteld bij bestaande BPM') return 'Added to existing BPM';
    return status === 'Actief' ? 'Active' : status;
}

function parseReason(value: any): string {
    const reason = normalise(value);
    if (reason === 'Opgeteld bij de actieve BPM-bron') return 'Added to the active BPM source';
    if (reason === 'Verborgen omdat al een actieve BPM-bron is gekoppeld') {
        return 'Hidden because an active BPM source is already linked';
    }
    return reason === 'Verborgen door gebruiker' ? 'Hidden by user' : reason;
}

function parseBoolean(value: any, defaultValue: boolean): boolean {
    const normalised = normalise(value).toLocaleLowerCase();
    if (!normalised) return defaultValue;
    if (['yes', 'ja', 'true', '1', 'x'].includes(normalised)) return true;
    if (['no', 'nee', 'false', '0'].includes(normalised)) return false;
    throw new Error(`Invalid yes/no value: ${value}`);
}

function normalise(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim();
}

function preferredWorkbookLanguage(): SubmeterMetadataLanguage {
    return AppCommonUtils.getPreferredLanguage()?.toLowerCase().split('-')[0] === 'nl' ? 'nl' : 'en';
}

function workbookLabels(language: string): SubmeterMetadataWorkbookLabels {
    return SUBMETER_METADATA_WORKBOOK_LABELS[language?.toLowerCase().split('-')[0] === 'nl' ? 'nl' : 'en'];
}

function hasSupportedHeaders(row: any[]): boolean {
    const headers = row.map(value => normalise(value).toLocaleLowerCase());
    return Object.values(SUBMETER_METADATA_WORKBOOK_LABELS).some(labels => {
        const expected = labels.headers.map(header => header.toLocaleLowerCase());
        const previousFull = expected.filter((_, index) => index !== 11);
        const previous = previousFull.slice(0, 14);
        const legacy = previousFull.slice(0, 11);
        return (headers.length === expected.length && expected.every((header, index) => headers[index] === header))
            || (headers.length === previousFull.length && previousFull.every((header, index) => headers[index] === header))
            || (headers.length === previous.length && previous.every((header, index) => headers[index] === header))
            || (headers.length === legacy.length && legacy.every((header, index) => headers[index] === header));
    });
}

function isReadOnlyColumn(column: number): boolean {
    return column < READ_ONLY_COLUMN_COUNT || READ_ONLY_TRAILING_COLUMNS.has(column);
}

function formatAddress(address: any): string {
    if (!address) return '';
    return [address.street, address.houseNumber, address.houseNumberAddition, address.postalCode, address.city]
        .filter(Boolean).join(' ');
}

function safeFilePart(value: string): string {
    return value.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, '-').toLowerCase();
}
